import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getOpsClassesMeta } from '../../../requests';
import { getOpsTrainerSchedule } from '../../../requests/opsTrainer';
import {
  btnGhost,
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pill,
  Toolbar,
} from '../ui';

function fmtRange(start: string | null, end: string | null): string {
  if (!start) return '—';
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const day = s.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const t = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return e ? `${day} · ${t(s)} – ${t(e)}` : `${day} · ${t(s)}`;
}

/**
 * The instructor's world, previewed from the staff side — v1 of the
 * trainer surface. Read-only on purpose: it shows what an instructor
 * login would show, before instructor logins exist.
 */
const OpsTrainer: React.FC = () => {
  const [instructor, setInstructor] = useState('');
  const [when, setWhen] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [account, setAccount] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [condensed, setCondensed] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const { data: meta } = useQuery({
    queryKey: ['opsClassesMeta'],
    queryFn: getOpsClassesMeta,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['opsTrainerSchedule', instructor, when, from, to],
    queryFn: () =>
      getOpsTrainerSchedule(instructor, {
        when,
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: Boolean(instructor),
  });

  return (
    <>
      <PageHeader
        title="Trainer view"
        meta="An instructor's classes, exactly as their own login shows them — upcoming, past or a date window, one account or all"
      />
      <Toolbar>
        <select
          className={inputClass}
          value={instructor}
          onChange={(e) => setInstructor(e.target.value)}
        >
          <option value="">Choose an instructor…</option>
          {meta?.instructors.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={when}
          onChange={(e) =>
            setWhen(e.target.value as 'upcoming' | 'past' | 'all')
          }
          title="Which classes"
        >
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
          <option value="all">All</option>
        </select>
        <input
          type="date"
          className={inputClass}
          title="From"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <input
          type="date"
          className={inputClass}
          title="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <select
          className={inputClass}
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          title="Only this account"
        >
          <option value="">All accounts</option>
          {Array.from(
            new Set(
              (data?.items ?? [])
                .map((c) => c.accountName)
                .filter(Boolean) as string[],
            ),
          )
            .sort()
            .map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-five">
          <input
            type="checkbox"
            className="ops-check"
            checked={condensed}
            onChange={(e) => setCondensed(e.target.checked)}
          />
          Condensed
        </label>
        {data && (
          <span className="text-sm text-gray-three">
            {
              data.items.filter((c) => !account || c.accountName === account)
                .length
            }{' '}
            shown
          </span>
        )}
      </Toolbar>
      <PageBody>
        {!instructor ? (
          <Card>
            <EmptyState
              title="Pick an instructor"
              hint="Their upcoming classes appear here with rosters one click away."
            />
          </Card>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : !data || data.items.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing upcoming"
              hint={`No scheduled classes from today onward for ${instructor}.`}
            />
          </Card>
        ) : (
          <div className="grid gap-3">
            {data.items
              .filter((c) => !account || c.accountName === account)
              .map((c) =>
                condensed && !open.has(c.id) ? (
                  <button
                    key={c.id}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-white-regular bg-white px-4 py-2 text-left text-sm hover:bg-blue-100/40"
                    onClick={() => setOpen((o) => new Set(o).add(c.id))}
                  >
                    <span className="min-w-0 truncate">
                      <span className="font-medium text-gray-four">
                        {c.name ?? '(unnamed class)'}
                      </span>
                      <span className="ml-2 text-xs text-gray-bold">
                        {fmtRange(c.start, c.end)}
                        {c.accountName ? ` · ${c.accountName}` : ''}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2 text-xs text-gray-bold">
                      {c.delivery?.prepCallStatus && (
                        <Pill value={c.delivery.prepCallStatus} />
                      )}
                      {c.registered} registered
                      <span>▾</span>
                    </span>
                  </button>
                ) : (
                  <Card key={c.id}>
                    <div className="flex flex-wrap items-center justify-between gap-3 p-5">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-gray-four">
                          {c.name ?? '(unnamed class)'}
                          {c.series?.count != null && (
                            <span className="ml-2 rounded bg-grey-100 px-1.5 py-0.5 text-[11px] font-medium text-grey-700">
                              {c.series.index}/{c.series.count}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-sm text-gray-three">
                          {fmtRange(c.start, c.end)}
                          {c.accountName ? ` · ${c.accountName}` : ''}
                          {c.location ? ` · ${c.location}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Pill value={c.status} />
                        <span className="text-sm text-gray-five [font-variant-numeric:tabular-nums]">
                          {c.registered} registered
                        </span>
                        <Link
                          className={btnGhost}
                          to={PATH.opsClassEdit.replace(':id', c.id)}
                        >
                          Class
                        </Link>
                        <Link
                          className={btnGhost}
                          to={`${PATH.opsRegistrations}?classId=${c.id}`}
                        >
                          Roster →
                        </Link>
                      </div>
                    </div>
                    {(c.locationAddress ||
                      c.onsiteContact ||
                      c.links?.courseOutline ||
                      c.reasonForSession ||
                      c.audienceInformation ||
                      c.recordingDetails) && (
                      <div className="grid gap-2 border-t border-white-three px-5 py-3 text-sm md:grid-cols-2">
                        {c.location === 'Onsite' && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              On site
                            </div>
                            <div>
                              {c.locationAddress ?? (
                                <span className="text-amber-800">
                                  address not set
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-five">
                              {c.onsiteContact
                                ? `Contact: ${c.onsiteContact}`
                                : 'On-site contact not set'}
                            </div>
                          </div>
                        )}
                        {c.links?.courseOutline && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              Outline
                            </div>
                            <a
                              className="text-blue-primary hover:underline"
                              href={c.links.courseOutline}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {c.links.courseOutline}
                            </a>
                          </div>
                        )}
                        {c.reasonForSession && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              Why this session
                            </div>
                            <div className="whitespace-pre-wrap text-xs text-gray-five">
                              {c.reasonForSession}
                            </div>
                          </div>
                        )}
                        {c.audienceInformation && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              Audience
                            </div>
                            <div className="whitespace-pre-wrap text-xs text-gray-five">
                              {c.audienceInformation}
                            </div>
                          </div>
                        )}
                        {c.recordingDetails && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              Recording
                            </div>
                            <div className="text-xs text-gray-five">
                              {c.recordingDetails}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {c.delivery &&
                      (c.delivery.prepCallStatus ||
                        c.delivery.materials?.status ||
                        c.delivery.travelStatus ||
                        c.delivery.customisationNotes ||
                        c.delivery.customisationHours != null) && (
                        <div className="flex flex-wrap items-start gap-x-6 gap-y-2 border-t border-white-three px-5 py-3 text-sm">
                          {c.delivery.prepCallStatus && (
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                                Prep call
                              </div>
                              <Pill value={c.delivery.prepCallStatus} />
                            </div>
                          )}
                          {c.delivery.materials?.status && (
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                                Materials
                              </div>
                              <Pill
                                value={c.delivery.materials.status}
                                tone={
                                  c.delivery.materials.status === 'Shipped'
                                    ? 'good'
                                    : c.delivery.materials.status === 'To ship'
                                    ? 'warn'
                                    : 'neutral'
                                }
                              />
                              {c.delivery.materials.trackingNote && (
                                <span className="ml-2 text-xs text-gray-five">
                                  {c.delivery.materials.trackingNote}
                                </span>
                              )}
                            </div>
                          )}
                          {c.delivery.travelStatus && (
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                                Travel
                              </div>
                              <Pill
                                value={c.delivery.travelStatus}
                                tone={
                                  c.delivery.travelStatus === 'Booked' ||
                                  c.delivery.travelStatus === 'Invoiced'
                                    ? 'good'
                                    : c.delivery.travelStatus === 'Not required'
                                    ? 'neutral'
                                    : 'warn'
                                }
                              />
                              {c.delivery.travelNotes && (
                                <div className="mt-1 max-w-md whitespace-pre-wrap text-xs text-gray-five">
                                  {c.delivery.travelNotes}
                                </div>
                              )}
                            </div>
                          )}
                          {(c.delivery.customisationNotes ||
                            c.delivery.customisationHours != null) && (
                            <div className="basis-full">
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-bold">
                                Customisation
                                {c.delivery.customisationHours != null &&
                                  ` · ${c.delivery.customisationHours} h`}
                              </div>
                              {c.delivery.customisationNotes && (
                                <div className="whitespace-pre-wrap text-gray-five">
                                  {c.delivery.customisationNotes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    {(c.notes.program?.info ||
                      c.notes.trainer ||
                      c.notes.payment) && (
                      <div className="grid gap-3 border-t border-white-three px-5 py-4 text-sm md:grid-cols-2">
                        {c.notes.program?.info && (
                          <div className="md:col-span-2">
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              From the program
                              {c.notes.program.programName
                                ? ` · ${c.notes.program.programName}`
                                : ''}
                            </div>
                            <div className="whitespace-pre-wrap text-gray-five">
                              {c.notes.program.info}
                            </div>
                          </div>
                        )}
                        {c.notes.trainer && (
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              Project notes
                            </div>
                            <div className="whitespace-pre-wrap text-gray-five">
                              {c.notes.trainer}
                            </div>
                          </div>
                        )}
                        {c.notes.payment && (
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-bold">
                              Payment notes
                            </div>
                            <div className="whitespace-pre-wrap text-gray-five">
                              {c.notes.payment}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {Object.values(c.links).some(Boolean) && (
                      <div className="flex flex-wrap gap-2 border-t border-white-three px-5 py-3 text-sm">
                        {(
                          [
                            ['learnerLink', 'Learner link'],
                            ['privateClassLink', 'Private class link'],
                            ['courseOutline', 'Course outline'],
                            ['studentSurvey', 'Student survey'],
                            ['instructorSurvey', 'Instructor survey'],
                            ['icalLink', 'Calendar'],
                          ] as const
                        ).map(([key, label]) =>
                          c.links[key] ? (
                            <a
                              key={key}
                              className="rounded-md border border-white-three px-2.5 py-1 text-xs text-blue-primary transition-colors hover:border-blue-500"
                              href={c.links[key] as string}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {label} ↗
                            </a>
                          ) : null,
                        )}
                      </div>
                    )}
                  </Card>
                ),
              )}
          </div>
        )}
      </PageBody>
    </>
  );
};

export default OpsTrainer;
